import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CheckCircleFill,
  XCircleFill,
  ArrowLeft,
  CreditCard2Front,
  CurrencyExchange,
  Phone,
} from "react-bootstrap-icons";
import { toast } from "../../../utilities/toast";
import {
  initiateB2BPayment,
  fetchStoreInfo,
  fetchActiveOrder,
} from "../../../lib/webService/webService";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";

if (typeof document !== "undefined" && !document.getElementById("tp-styles")) {
  const s = document.createElement("style");
  s.id = "tp-styles";
  s.textContent = `
    .tp-wrap{min-height:100vh;background:#F2F2F7;display:flex;align-items:center;justify-content:center;padding:24px 16px;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Helvetica Neue",Arial,sans-serif}
    .tp-card{background:#fff;border-radius:20px;padding:28px 24px;width:100%;max-width:420px;box-shadow:0 2px 16px rgba(0,0,0,.07)}
    .tp-back{display:flex;align-items:center;gap:6px;background:none;border:none;color:#007AFF;font-size:.9375rem;font-weight:500;cursor:pointer;padding:0;font-family:inherit;margin-bottom:20px}
    .tp-progress-track{height:6px;background:rgba(118,118,128,.15);border-radius:3px;overflow:hidden;margin-bottom:8px}
    .tp-progress-fill{height:100%;background:#007AFF;border-radius:3px;transition:width .5s ease}
    .tp-detail-row{display:flex;align-items:center;gap:14px;padding:12px 0;border-bottom:1px solid rgba(60,60,67,.07)}
    .tp-detail-row:last-child{border-bottom:none}
    .tp-detail-icon{width:36px;height:36px;border-radius:10px;background:rgba(0,122,255,.10);display:flex;align-items:center;justify-content:center;flex-shrink:0}
    .tp-btn{padding:13px 24px;border:none;border-radius:12px;font-size:.9375rem;font-weight:600;cursor:pointer;font-family:inherit;transition:opacity .15s}
    .tp-btn-primary{background:#007AFF;color:#fff}
    .tp-btn-primary:active{opacity:.82}
    .tp-btn-ghost{background:rgba(118,118,128,.10);color:#1c1c1e}
    @keyframes _tp_spin{to{transform:rotate(360deg)}}
    ._tp_spin{width:44px;height:44px;border:3px solid rgba(0,122,255,.2);border-top-color:#007AFF;border-radius:50%;animation:_tp_spin .9s linear infinite;margin:0 auto 20px}
  `;
  document.head.appendChild(s);
}

const FONT = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif`;

const TransactionProgress = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [transactionStatus, setTransactionStatus] = useState("processing");
  const [progress, setProgress] = useState(0);
  const [transactionId, setTransactionId] = useState("");
  const paymentInitiated = useRef(false);

  useEffect(() => {
    console.log("Location state:", location.state);
    const checkAndProcessPayment = async () => {
      if (paymentInitiated.current) return;
      paymentInitiated.current = true;

      try {
        setProgress(10);

        const activeOrder = await fetchActiveOrder(
          location.state?.rid,
          location.state?.orderId,
          location.state?.token
        );
        const isOrderPaid = activeOrder.is_paid;

        if (isOrderPaid) {
          setProgress(100);
          setTransactionStatus("success");
          return;
        }

        const storeInfo = await fetchStoreInfo(location.state?.storeId);
        console.log(storeInfo);
        const paymentData = {
          name: storeInfo.name,
          account: storeInfo.account,
          account_type: storeInfo.account_type,
          ...(storeInfo.account_ref !== "" && {
            account_reference: storeInfo.account_ref,
          }),
          amount: 10,
          narrative: "Store Payment",
        };
        setProgress(30);

        const response = await initiateB2BPayment(
          [paymentData],
          paymentData.account_type,
          location.state?.orderId,
          location.state?.rid,
          location.state?.token
        );
        setTransactionId(response.data.transactions[0].transaction_id);
        console.log("Response:", response);

        setProgress(100);
        setTransactionStatus("success");
      } catch (error) {
        setTransactionStatus("error");
        toast.error(error.message || "Payment failed. Please try again.");
      }
    };

    checkAndProcessPayment();
  }, []);

  const handleRollback = () => navigate("/ops/");

  const renderContent = () => {
    switch (transactionStatus) {
      case "processing":
        return (
          <div style={{ textAlign: "center" }}>
            <div className="_tp_spin" />
            <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", marginBottom: 8 }}>
              Processing Payment
            </div>
            <div style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", marginBottom: 20, lineHeight: 1.5 }}>
              Please wait while we process your payment...
            </div>
            <div className="tp-progress-track">
              <div className="tp-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <div style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.45)", marginTop: 6 }}>
              {progress}% complete
            </div>
          </div>
        );

      case "success":
        return (
          <div style={{ textAlign: "center" }}>
            <CheckCircleFill size={52} color="#34C759" style={{ marginBottom: 16 }} />
            <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", marginBottom: 6 }}>
              Payment Successful
            </div>
            <div style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", marginBottom: 24, lineHeight: 1.5 }}>
              Your payment has been processed. The store has been notified.
            </div>

            <div style={{ background: "rgba(118,118,128,.07)", borderRadius: 14, padding: "4px 16px", marginBottom: 24, textAlign: "left" }}>
              <div className="tp-detail-row">
                <div className="tp-detail-icon"><CreditCard2Front size={18} color="#007AFF" /></div>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "rgba(60,60,67,0.5)", marginBottom: 2 }}>Transaction ID</div>
                  <div style={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1c1c1e" }}>#{transactionId}</div>
                </div>
              </div>
              <div className="tp-detail-row">
                <div className="tp-detail-icon"><CurrencyExchange size={18} color="#007AFF" /></div>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "rgba(60,60,67,0.5)", marginBottom: 2 }}>Amount Paid</div>
                  <div style={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1c1c1e" }}>
                    KES {location.state?.amount?.toLocaleString() || "0"}
                  </div>
                </div>
              </div>
              <div className="tp-detail-row">
                <div className="tp-detail-icon"><Phone size={18} color="#007AFF" /></div>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "rgba(60,60,67,0.5)", marginBottom: 2 }}>Payment Method</div>
                  <div style={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1c1c1e" }}>M-Pesa</div>
                </div>
              </div>
            </div>

            <button className="tp-btn tp-btn-primary" onClick={handleRollback} style={{ width: "100%" }}>
              Return to Orders
            </button>
          </div>
        );

      case "error":
        return (
          <div style={{ textAlign: "center" }}>
            <XCircleFill size={52} color="#FF3B30" style={{ marginBottom: 16 }} />
            <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", marginBottom: 6 }}>
              Payment Failed
            </div>
            <div style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", marginBottom: 24, lineHeight: 1.5 }}>
              We couldn't process your payment. Please try again or contact support.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                className="tp-btn tp-btn-primary"
                onClick={() => { setTransactionStatus("processing"); setProgress(0); }}
                style={{ width: "100%" }}
              >
                Try Again
              </button>
              <button className="tp-btn tp-btn-ghost" onClick={handleRollback} style={{ width: "100%" }}>
                Return to Orders
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="tp-wrap">
      <Seo {...generateNoIndexSEO("Delivery Progress")} />
      <div className="tp-card">
        <button className="tp-back" onClick={handleRollback}>
          <ArrowLeft size={18} /> Back
        </button>
        <div style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e", marginBottom: 24 }}>
          Payment Transaction
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default TransactionProgress;
